import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AppUser,
  Project,
  ProjectPriority,
  ProjectTemplate,
  Task,
  TemplateTask,
  TemplateWorkstream,
  Workstream,
} from '../pm/types';
import {
  createProject,
  createProjectTemplate,
  subscribeAllUsers,
  subscribeProjects,
  subscribeProjectTemplates,
  subscribeTasks,
  subscribeTemplateTasks,
  subscribeTemplateWorkstreams,
  subscribeWorkstreams,
  updateProject,
} from '../pm/firebase';
import {
  createSampleProject,
  SAMPLE_PROJECT_DESCRIPTION,
  SAMPLE_PROJECT_TITLE,
} from '../pm/seed';
import { getCurrentUser } from '../firebase';
import RoadmapView from '../components/pm/RoadmapView';
import MyWorkView from '../components/pm/MyWorkView';
import TemplatesListView from '../components/pm/TemplatesListView';
import GridView from '../components/pm/GridView';
import BacklogRail from '../components/pm/BacklogRail';
import BacklogPeekTab from '../components/pm/BacklogPeekTab';
import DoneRail from '../components/pm/DoneRail';
import DonePeekTab from '../components/pm/DonePeekTab';
import ViewBoundary from '../components/pm/ViewBoundary';
import ProjectDrawer from '../components/pm/ProjectDrawer';
import PmSuiteLayout from '../components/pm/PmSuiteLayout';
import { PmSection } from '../components/pm/SubRail';
// IconArchive removed alongside the top "Backlog · N" button; the side
// peek tab carries its own icon now.

// Sub-views inside the "Projects" section only.
type ProjectsView = 'grid' | 'roadmap';

const VIEW_PATHS: Record<ProjectsView, string> = {
  grid:    '/Projects',
  roadmap: '/Projects/Roadmap',
};

const VIEWS: { value: ProjectsView; label: string }[] = [
  { value: 'grid',    label: 'Grid'    },
  { value: 'roadmap', label: 'Roadmap' },
];

// Resolve the URL into (section, view). My work + Templates have no view;
// only the Projects section has Grid/Roadmap.
function routeFor(pathname: string): { section: PmSection; view: ProjectsView } {
  if (pathname.startsWith('/Projects/MyWork')) return { section: 'mywork', view: 'grid' };
  if (pathname.startsWith('/Projects/Templates')) return { section: 'templates', view: 'grid' };
  if (pathname.startsWith('/Projects/Roadmap')) return { section: 'projects', view: 'roadmap' };
  return { section: 'projects', view: 'grid' };
}

// Path the URL bar should show for a given (section, view). Used purely
// for cosmetic URL sync via window.history.replaceState — React Router
// and IonRouterOutlet never see these changes, which is the whole point.
function urlFor(s: PmSection, v: ProjectsView): string {
  if (s === 'mywork') return '/Projects/MyWork';
  if (s === 'templates') return '/Projects/Templates';
  return v === 'roadmap' ? '/Projects/Roadmap' : '/Projects';
}

const ProjectsList: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  // section/view are component state — NOT URL-derived during the page's
  // lifetime. This is the fix for the intermittent "container goes blank"
  // bug: every URL change React Router sees here makes IonRouterOutlet
  // run its page-stack lifecycle, and the same Route matching multiple
  // URLs sometimes left the outlet rendering nothing. By keeping section
  // changes in component state we never trigger that lifecycle on
  // sub-rail clicks.
  //
  // We still read the URL ONCE on mount so deep-link entries (someone
  // pasting /Projects/MyWork) land on the right section. After that the
  // URL is updated via raw window.history.replaceState so the bar stays
  // shareable without notifying the router.
  const initial = routeFor(location.pathname);
  const [section, setSectionState] = useState<PmSection>(initial.section);
  const [view, setViewState] = useState<ProjectsView>(initial.view);

  // Cosmetic URL sync — replaceState bypasses React Router. Skips when
  // the URL is already correct so we don't push duplicate entries.
  function syncUrl(nextSection: PmSection, nextView: ProjectsView) {
    const next = urlFor(nextSection, nextView);
    if (window.location.pathname !== next) {
      try { window.history.replaceState(null, '', next); } catch { /* ignore */ }
    }
  }

  function setSection(next: PmSection) {
    setSectionState(next);
    // Roadmap is meaningless outside the Projects section — reset to grid
    // when leaving so a re-entry doesn't land on a hidden state.
    if (next !== 'projects') setViewState('grid');
    syncUrl(next, next === 'projects' ? view : 'grid');
  }

  function setView(next: ProjectsView) {
    setViewState(next);
    syncUrl(section, next);
  }

  // Back / forward keep working: popstate fires when the user hits them,
  // and we sync our component state from the URL. (Forward navigation
  // out of /Projects/* still goes through React Router normally.)
  useEffect(() => {
    const onPop = () => {
      const next = routeFor(window.location.pathname);
      setSectionState(next.section);
      setViewState(next.view);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const [uid, setUid] = useState<string>('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [workstreams, setWorkstreams] = useState<Workstream[]>([]);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [templateWorkstreams, setTemplateWorkstreams] = useState<TemplateWorkstream[]>([]);
  const [templateTasks, setTemplateTasks] = useState<TemplateTask[]>([]);
  const [admins, setAdmins] = useState<AppUser[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [drawerProjectId, setDrawerProjectId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then((u: any) => {
        setUid(u?.uid || '');
        setAuthLoading(false);
      })
      .catch(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    const unsubP = subscribeProjects(setProjects);
    const unsubT = subscribeTasks(setTasks);
    const unsubW = subscribeWorkstreams(setWorkstreams);
    const unsubTpl = subscribeProjectTemplates(setTemplates);
    const unsubTplWs = subscribeTemplateWorkstreams(setTemplateWorkstreams);
    const unsubTplTk = subscribeTemplateTasks(setTemplateTasks);
    const unsubA = subscribeAllUsers((users) => setAdmins(users.filter((u) => u.isOperator)));
    return () => {
      unsubP();
      unsubT();
      unsubW();
      unsubTpl();
      unsubTplWs();
      unsubTplTk();
      unsubA();
    };
  }, []);

  // Section changes collapse the create-form — its action label depends on
  // section, and a stale open form across navigation is confusing. We also
  // collapse the Backlog rail since it only makes sense in the Projects
  // section.
  useEffect(() => {
    setShowForm(false);
    if (section !== 'projects') {
      setBacklogOpen(false);
      setDoneOpen(false);
    }
  }, [section]);

  // Active projects: neither parked in the backlog nor marked done. The
  // Backlog rail shows the parked ones and the Done rail the completed ones;
  // here we hide both so the main views read as "what's actually in motion."
  const activeProjects = useMemo(
    () => projects.filter((p) => !p.backlogged && !p.done),
    [projects],
  );

  // Count matches BacklogRail's pool: explicitly parked OR unprioritized, but
  // never done (those move to the Done rail). The Grid drops the unprioritized
  // fallback lane, so the rail is the only place those projects appear — the
  // count needs to reflect that or the peek tab will under-report.
  const backlogCount = useMemo(
    () => projects.filter((p) => !p.done && (p.backlogged || !p.priority)).length,
    [projects],
  );

  // Count matches DoneRail's pool: every project flagged done.
  const doneCount = useMemo(
    () => projects.filter((p) => p.done).length,
    [projects],
  );

  // ---------- Project drag state (Grid ⇄ Backlog) ----------
  // Hoisted to this page because both the Grid lanes and the Backlog peek
  // tab need to react to a drag in flight: lanes show drop outlines and the
  // tab thickens into a drop target. We carry the source so dropping back
  // into the same lane is a no-op rather than a useless write.
  type DragSource = 'grid' | 'backlog' | 'done';
  interface DragProject {
    id: string;
    source: DragSource;
    /** Originating priority lane when dragged from Grid; undefined when from
     *  a rail (rail items may be unprioritized). */
    sourcePriority?: ProjectPriority;
  }
  const [dragProject, setDragProject] = useState<DragProject | null>(null);
  const [peekDragOver, setPeekDragOver] = useState(false);
  const [donePeekDragOver, setDonePeekDragOver] = useState(false);

  function startProjectDrag(d: DragProject) {
    setDragProject(d);
  }
  function endProjectDrag() {
    setDragProject(null);
    setPeekDragOver(false);
    setDonePeekDragOver(false);
  }
  // Lane drop — set priority and clear the backlog/done flags when the card
  // came from a rail (dropping onto an active lane reactivates it). No-op if
  // nothing actually changes (avoids a write that does nothing).
  async function dropOnLane(target: ProjectPriority) {
    const d = dragProject;
    endProjectDrag();
    if (!d) return;
    const project = projects.find((p) => p.id === d.id);
    if (!project) return;
    const patch: { priority?: ProjectPriority; backlogged?: null; done?: null } = {};
    if (project.priority !== target) patch.priority = target;
    if (project.backlogged) patch.backlogged = null;
    if (project.done) patch.done = null;
    if (Object.keys(patch).length === 0) return;
    try {
      await updateProject(d.id, patch);
    } catch {
      /* errors propagate through the realtime subscription */
    }
  }
  // Backlog peek drop — park the project. Clears done so it lives in exactly
  // one rail. Already backlogged → no-op so there's no phantom write.
  async function dropOnPeek() {
    const d = dragProject;
    endProjectDrag();
    if (!d) return;
    const project = projects.find((p) => p.id === d.id);
    if (!project || project.backlogged) return;
    try {
      await updateProject(d.id, { backlogged: true, done: null });
    } catch {
      /* swallow — subscription will reflect failure */
    }
  }
  // Done peek drop — mark the project complete. Clears backlog for the same
  // single-rail invariant. Already done → no-op.
  async function dropOnDonePeek() {
    const d = dragProject;
    endProjectDrag();
    if (!d) return;
    const project = projects.find((p) => p.id === d.id);
    if (!project || project.done) return;
    try {
      await updateProject(d.id, { done: true, backlogged: null });
    } catch {
      /* swallow — subscription will reflect failure */
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (section === 'templates') {
        const id = await createProjectTemplate({ title, description }, uid);
        setTitle('');
        setDescription('');
        setShowForm(false);
        history.push(`/Projects/Templates/${id}`);
      } else {
        const id = await createProject({ title, description }, uid);
        setTitle('');
        setDescription('');
        setShowForm(false);
        history.push(`/Projects/${id}`);
      }
    } catch (err: any) {
      // Log the full error to the console so the Firebase error code + path
      // are visible in DevTools (e.g. PERMISSION_DENIED at /projectTemplates).
      // The on-screen banner stays concise but includes the code when
      // available so the user can see what kind of failure happened.
      // eslint-disable-next-line no-console
      console.error('Project/template create failed', err);
      const code = err?.code ? ` [${err.code}]` : '';
      const message = err?.message || (section === 'templates' ? 'Could not create template.' : 'Could not create project.');
      setError(`${message}${code}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateSample() {
    setBusy(true);
    setError(null);
    try {
      const id = await createSampleProject(
        SAMPLE_PROJECT_TITLE,
        SAMPLE_PROJECT_DESCRIPTION,
        uid,
      );
      history.push(`/Projects/${id}`);
    } catch (err: any) {
      setError(err?.message || 'Could not create sample project.');
    } finally {
      setBusy(false);
    }
  }

  // The page header label + subtitle are section-specific. My work and
  // Templates don't show a primary action button — their actions live
  // inside the section bodies (status pickers, "Use template" cards).
  const headerCopy: Record<PmSection, { title: string; sub: string | null }> = {
    projects:  { title: 'Projects',  sub: 'Shared admin workspace · real-time across signed-in admins' },
    mywork:    { title: 'My work',   sub: null },
    templates: { title: 'Templates', sub: null },
  };
  const headerActionLabel =
    section === 'templates' ? 'New template'
    : section === 'projects' ? 'New project'
    : null;
  const createFormTitlePlaceholder =
    section === 'templates' ? 'Template title' : 'Project title';
  const createFormSubmitLabel =
    section === 'templates' ? 'Create template' : 'Create project';

  if (authLoading) {
    return (
      <PmSuiteLayout active={section} onSectionChange={setSection}>
        <div className="pm-loading">Loading…</div>
      </PmSuiteLayout>
    );
  }

  return (
    <PmSuiteLayout active={section} onSectionChange={setSection}>
      <div className="pm-page">
        <div className="pm-header">
          <div className="pm-header-titles">
            <h1>{headerCopy[section].title}</h1>
            {headerCopy[section].sub ? <p>{headerCopy[section].sub}</p> : null}
          </div>
          {/* The "Backlog · N" toggle moved out of the header — it's now the
              side-attached BacklogPeekTab rendered at page level (below).
              Keeping only the section CTA here (e.g. "New project"). */}
          {headerActionLabel ? (
            <div className="pm-header-actions">
              <button
                className="pm-button pm-button-primary"
                onClick={() => setShowForm((v) => !v)}
              >
                {showForm ? 'Cancel' : headerActionLabel}
              </button>
            </div>
          ) : null}
        </div>

        {section === 'projects' ? (
          <div className="pm-projects-tabs" role="tablist" aria-label="Projects views">
            {VIEWS.map((v) => {
              const active = v.value === view;
              return (
                <button
                  key={v.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`pm-projects-tab ${active ? 'active' : ''}`}
                  // State-only: setView() updates local state and quietly
                  // syncs the URL via raw replaceState. We no longer call
                  // history.* here because IonRouterOutlet reacts to ALL
                  // location changes (including replace) by running its
                  // page-transition lifecycle, which intermittently left
                  // the container blank.
                  onClick={() => setView(v.value)}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        ) : null}

        {error ? <div className="pm-error">{error}</div> : null}

        {showForm && headerActionLabel ? (
          <form className="pm-create-form" onSubmit={handleCreate}>
            <input
              placeholder={createFormTitlePlaceholder}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
            <textarea
              placeholder="Short description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
            <div>
              <button
                className="pm-button pm-button-primary"
                type="submit"
                disabled={busy || !title.trim()}
              >
                {busy ? 'Creating…' : createFormSubmitLabel}
              </button>
            </div>
          </form>
        ) : null}

        {/* Each section view is wrapped in a ViewBoundary so a crash inside
            (e.g. the intermittent Roadmap blanking we were chasing) no
            longer wipes out the entire right-side container — the user sees
            a small fallback in just that slot and can navigate away. The
            `viewKey` prop resets the boundary when the user changes tabs. */}
        <ViewBoundary viewKey={`${section}:${view}`}>
          {section === 'projects' && view === 'grid' ? (
            activeProjects.length === 0 ? (
              <div className="pm-empty">
                <h3>No projects yet</h3>
                <p>Create one above, or load a generic sample project to see how the workspace is structured.</p>
                <button className="pm-button" onClick={handleCreateSample} disabled={busy}>
                  {busy ? 'Creating…' : 'Load sample project'}
                </button>
              </div>
            ) : (
              <GridView
                uid={uid}
                projects={activeProjects}
                tasks={tasks}
                admins={admins}
                dragProject={dragProject}
                onProjectDragStart={(id, priority) =>
                  startProjectDrag({ id, source: 'grid', sourcePriority: priority })
                }
                onProjectDragEnd={endProjectDrag}
                onDropOnLane={dropOnLane}
              />
            )
          ) : null}

          {section === 'projects' && view === 'roadmap' ? (
            <RoadmapView
              projects={activeProjects}
              tasks={tasks}
              admins={admins}
              onOpenDrawer={(id) => setDrawerProjectId(id)}
            />
          ) : null}

          {section === 'mywork' ? (
            <MyWorkView
              uid={uid}
              tasks={tasks}
              projects={projects}
              workstreams={workstreams}
              admins={admins}
            />
          ) : null}

          {section === 'templates' ? (
            <TemplatesListView
              uid={uid}
              templates={templates}
              templateWorkstreams={templateWorkstreams}
              templateTasks={templateTasks}
            />
          ) : null}
        </ViewBoundary>
      </div>
      {section === 'projects' ? (
        <>
          <BacklogRail
            open={backlogOpen}
            onClose={() => setBacklogOpen(false)}
            projects={projects}
            tasks={tasks}
            onProjectDragStart={(id) => {
              // The rail at 360px wide otherwise covers the right-side
              // Grid lanes (esp. Low priority) while the user is trying to
              // drop. Close the rail at drag-start; the drag image stays
              // floating with the cursor, and the user lands on a fully
              // visible Grid. The peek tab is still reachable on the side
              // if they cancel and want to grab another item.
              setBacklogOpen(false);
              startProjectDrag({ id, source: 'backlog' });
            }}
            onProjectDragEnd={endProjectDrag}
          />
          <BacklogPeekTab
            count={backlogCount}
            open={backlogOpen}
            onToggle={() => setBacklogOpen((o) => {
              // Only one rail open at a time — they'd overlap on narrow
              // viewports otherwise.
              if (!o) setDoneOpen(false);
              return !o;
            })}
            dragActive={!!dragProject}
            dragOverHere={peekDragOver}
            onDragOver={(e) => {
              // Only declare ourselves a drop target when there's actually a
              // project drag in flight — otherwise random drags (browser
              // text, files) would wrongly target the peek tab.
              if (!dragProject) return;
              e.preventDefault();
              if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
              if (!peekDragOver) setPeekDragOver(true);
            }}
            onDragLeave={() => setPeekDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              dropOnPeek();
            }}
          />
          <DoneRail
            open={doneOpen}
            onClose={() => setDoneOpen(false)}
            projects={projects}
            tasks={tasks}
            onProjectDragStart={(id) => {
              // Close the rail at drag-start so the 360px panel doesn't cover
              // the left-most Grid lane the user is dropping onto. The drag
              // image stays with the cursor.
              setDoneOpen(false);
              startProjectDrag({ id, source: 'done' });
            }}
            onProjectDragEnd={endProjectDrag}
          />
          <DonePeekTab
            count={doneCount}
            open={doneOpen}
            onToggle={() => setDoneOpen((o) => {
              if (!o) setBacklogOpen(false);
              return !o;
            })}
            dragActive={!!dragProject}
            dragOverHere={donePeekDragOver}
            onDragOver={(e) => {
              if (!dragProject) return;
              e.preventDefault();
              if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
              if (!donePeekDragOver) setDonePeekDragOver(true);
            }}
            onDragLeave={() => setDonePeekDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              dropOnDonePeek();
            }}
          />
        </>
      ) : null}

      <ProjectDrawer
        projectId={drawerProjectId}
        projects={projects}
        tasks={tasks}
        workstreams={workstreams}
        admins={admins}
        onClose={() => setDrawerProjectId(null)}
      />
    </PmSuiteLayout>
  );
};

export default ProjectsList;
